import { Button, FieldError, SelectMenu } from '@postscript/components';
import ChooseFlowTemplateOption from 'components/flowBuilder/components/forms/ChooseFlowTemplateOption';
import {
  FlowTemplateTypesMap,
  FlowTypes,
} from 'components/flowBuilder/constants';
import { FlowWizardOptions } from 'components/flowBuilder/constants/trackingEvents';
import useFlowTemplates from 'components/flowBuilder/hooks/useFlowTemplates';
import {
  FlowTemplate,
  FlowType,
  SelectMenuOption,
  SubmitCreateFlowArgs,
} from 'components/flowBuilder/types';
import Loader from 'components/generic/Loading/Loader';
import { Form, Formik } from 'formik';
import { filter, uniqBy } from 'lodash';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

const StyledButtons = styled.div`
  display: flex;
  gap: var(--spacing-2);
  justify-content: flex-end;
  padding: var(--spacing-4) 0 0 0;
  position: relative;
  & button {
    width: fit-content;
  }
`;

const StyledErrorsWrapper = styled.div`
  padding: 0 var(--spacing-5) var(--spacing-4);
`;

const StyledOptionsWrapper = styled.div<{ variant?: 'modal' | 'page' }>`
  display: grid;
  gap: var(--spacing-3);
  grid-template-columns: repeat(2, 1fr);
  max-height: ${({ variant }) => (variant === 'modal' ? '282px' : '400px')};
  min-height: 282px;
  overflow: auto;
  padding: var(--spacing-1);
`;

const StyledSelectMenu = styled(SelectMenu)`
  margin-bottom: var(--spacing-3);
  width: 350px;
`;

const StyledShadow = styled.div`
  height: 1px;
  width: 100%;
  box-shadow: 0px 4px 12px 0px #a1a1a1;
`;

const ALL_TEMPLATES_FILTER = 'All Templates';

interface ChooseTemplateFormProps {
  flowType: FlowType;
  handleSubmit: (args: SubmitCreateFlowArgs) => void;
  initialTemplate?: FlowTemplate;
  onBack?: () => void;
  variant?: 'modal' | 'page';
}

export interface FormValues {
  templateId: number;
  type: FlowType;
}

const validationSchema: Yup.SchemaOf<FormValues> = Yup.object({
  templateId: Yup.number()
    .positive('Select a template from the list')
    .required(),
  type: Yup.mixed<FlowType>().required('Type'),
});

export default function ChooseFlowTemplateForm(props: ChooseTemplateFormProps) {
  const { handleSubmit, initialTemplate, flowType, onBack, variant } = props;

  const { data: templates, isLoading } = useFlowTemplates(
    FlowTemplateTypesMap[flowType].templateType,
  );

  const [template, setTemplate] = useState<FlowTemplate | undefined>(
    initialTemplate,
  );

  const [selectedFilter, setSelectedFilter] = useState(ALL_TEMPLATES_FILTER);

  const createTemplateCategoryOption = (
    category: string,
  ): SelectMenuOption => ({
    label: category,
    value: category,
  });

  const templateIdChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const templateId = parseInt(e.currentTarget.value);
    const template = templates.find(({ id }) => id === templateId);
    if (!template) return;
    setTemplate(template);
  };

  const options = useMemo(() => {
    const uniqueCategories = uniqBy(templates, 'category');
    return [
      createTemplateCategoryOption(ALL_TEMPLATES_FILTER),
      ...uniqueCategories.map((c) => createTemplateCategoryOption(c.category)),
    ];
  }, [templates]);

  const filteredTemplates =
    selectedFilter === ALL_TEMPLATES_FILTER
      ? templates
      : filter(templates, ['category', selectedFilter]);

  return (
    <div>
      {isLoading && <Loader>Loading...</Loader>}
      {!isLoading && templates && (
        <Formik
          initialValues={{ templateId: template?.id ?? '', type: flowType }}
          onSubmit={() =>
            handleSubmit({
              from:
                flowType === FlowTypes.CAMPAIGN
                  ? FlowWizardOptions.CAMPAIGN_TEMPLATE
                  : FlowWizardOptions.AUTOMATION_TEMPLATE,
              template,
            })
          }
          validationSchema={validationSchema}
        >
          {(form) => {
            const errors = Object.entries(form.errors);
            return (
              <Form>
                <StyledSelectMenu
                  isClearable={false}
                  label="Template category"
                  name="template-category"
                  options={options}
                  value={createTemplateCategoryOption(selectedFilter)}
                  onChange={(option) => {
                    setSelectedFilter((option as SelectMenuOption).value);
                  }}
                />
                <StyledOptionsWrapper variant={variant}>
                  {filteredTemplates.map(
                    ({ data: { description }, id, category, name }) => (
                      <ChooseFlowTemplateOption
                        description={description}
                        key={id}
                        onChange={templateIdChangeHandler}
                        tag={category}
                        title={name}
                        value={id}
                      />
                    ),
                  )}
                </StyledOptionsWrapper>
                <input hidden name="type" readOnly value={flowType} />
                {errors.length > 0 && (
                  <StyledErrorsWrapper>
                    {errors.map(([name, error]) => (
                      <FieldError htmlFor={name} key={name}>
                        {error}
                      </FieldError>
                    ))}
                  </StyledErrorsWrapper>
                )}
                <StyledShadow />
                <StyledButtons>
                  {onBack && (
                    <Button variant="secondary" onClick={onBack}>
                      Back
                    </Button>
                  )}
                  <Button variant="primary" type="submit">
                    Continue
                  </Button>
                </StyledButtons>
              </Form>
            );
          }}
        </Formik>
      )}
    </div>
  );
}
